// All available add-ons in the system
export const ALL_ADDONS = [
  'BODY COMPOSITION ANALYSIS',
  'GUT MICROBIOME TEST',
  'METABOLIC RISK PROFILING',
  'VO2 MAX & FITNESS TEST',
  'METABOLOMICS',
  'LIPOMICS',
  'GENOME 360',
  'HEAVY METAL TEST',
  'FOOD ALLERGY TEST',
  'SIBO',
  'EPIGENETIC AGE TESTING',
] as const;

// Add-ons included with each program tier
export const PROGRAM_INCLUDED_ADDONS = {
  'THRIVE EARLY': [],
  'THRIVE FOUNDATION': [],
  'THRIVE CORE': [
    'HEAVY METAL TEST',
    'BODY COMPOSITION ANALYSIS',
    'METABOLIC RISK PROFILING',
    'LIPOMICS',
  ],
  'THRIVE LIMITLESS': [
    'HEAVY METAL TEST',
    'BODY COMPOSITION ANALYSIS',
    'METABOLIC RISK PROFILING',
    'VO2 MAX & FITNESS TEST',
    'GUT MICROBIOME TEST',
    'LIPOMICS',
  ],
} as const;

// Configuration for which steps to show for each program
export const PROGRAM_STEPS_CONFIG = {
  'THRIVE EARLY': {
    steps: ['profile', 'thyrocare', 'thrive_check', 'neurowyzer', 'genepowerx'],
  },
  'THRIVE FOUNDATION': {
    steps: [
      'profile',
      'thyrocare',
      'upload_reports',
      'thrive_check',
      'intake_call',
      'neurowyzer',
      'genepowerx',
      'in_center',
      'longevity_consultation',
    ],
  },
  'THRIVE CORE': {
    steps: [
      'profile',
      'thyrocare',
      'upload_reports',
      'thrive_check',
      'intake_call',
      'neurowyzer',
      'genepowerx',
      'in_center',
      'longevity_consultation',
    ],
  },
  'THRIVE LIMITLESS': {
    steps: [
      'profile',
      'thyrocare',
      'upload_reports',
      'thrive_check',
      'intake_call',
      'neurowyzer',
      'genepowerx',
      'in_center',
      'longevity_consultation',
    ],
  },
  CONTINUUM: {
    steps: [
      'profile',
      'upload_reports',
      'thrive_check',
      'intake_call',
      'neurowyzer',
      'genepowerx',
      'in_center',
      'longevity_consultation',
    ],
  },
} as const;

// Helper function to get steps for a program
export const getStepsForProgram = (programName: string): readonly string[] => {
  const normalizedName = programName.toUpperCase().trim();

  if (normalizedName === 'THRIVE EARLY') {
    return PROGRAM_STEPS_CONFIG['THRIVE EARLY'].steps;
  }

  if (normalizedName === 'THRIVE FOUNDATION') {
    return PROGRAM_STEPS_CONFIG['THRIVE FOUNDATION'].steps;
  }

  if (normalizedName === 'THRIVE CORE') {
    return PROGRAM_STEPS_CONFIG['THRIVE CORE'].steps;
  }

  if (normalizedName === 'THRIVE LIMITLESS') {
    return PROGRAM_STEPS_CONFIG['THRIVE LIMITLESS'].steps;
  }

  if (normalizedName === 'CONTINUUM') {
    return PROGRAM_STEPS_CONFIG.CONTINUUM.steps;
  }

  // Default to all steps if program not found
  return [
    'profile',
    'thyrocare',
    'upload_reports',
    'thrive_check',
    'intake_call',
    'neurowyzer',
    'genepowerx',
    'in_center',
    'longevity_consultation',
  ];
};

// Helper function to get included add-ons for a program
export const getIncludedAddonsForProgram = (programName: string): readonly string[] => {
  const normalizedName = programName.toUpperCase().trim();
  return PROGRAM_INCLUDED_ADDONS[normalizedName as keyof typeof PROGRAM_INCLUDED_ADDONS] || [];
};

// Helper function to extract add-on name from checklist task title
export const extractAddonNameFromTaskTitle = (taskTitle: string): string | null => {
  // Remove "ADD ON " prefix if present
  const cleanTitle = taskTitle.replace(/^ADD ON /, '');

  // Map common task title variations to standardized add-on names
  const titleMappings: Record<string, string> = {
    SIBO: 'SIBO',
    METABOLOMICS: 'METABOLOMICS',
    'GUT MICROBIOME TEST': 'GUT MICROBIOME TEST',
    'FOOD ALLERGY TEST': 'FOOD ALLERGY TEST',
    'GENOME 360': 'GENOME 360',
    'HEAVY METAL TEST': 'HEAVY METAL TEST',
    'BODY COMPOSITION ANALYSIS': 'BODY COMPOSITION ANALYSIS',
    'METABOLIC RISK PROFILING': 'METABOLIC RISK PROFILING',
    'VO2 MAX & FITNESS TEST': 'VO2 MAX & FITNESS TEST',
    LIPOMICS: 'LIPOMICS',
    'EPIGENETIC AGE TESTING': 'EPIGENETIC AGE TESTING',
    // Handle variations in task titles
    'HEAVY METAL TEST - THROUGH PHOTOMETRY, NON-INVASIVE': 'HEAVY METAL TEST',
    'METABOLIC RISK PROFILER': 'METABOLIC RISK PROFILING',
  };

  // Find matching add-on name
  for (const [pattern, addonName] of Object.entries(titleMappings)) {
    if (cleanTitle.includes(pattern) || pattern.includes(cleanTitle)) {
      return addonName;
    }
  }

  return null;
};

// Helper function to check if a user has purchased a specific add-on
export const hasUserPurchasedAddon = (
  userAddonNames: string[] | undefined,
  addonName: string
): boolean => {
  if (!userAddonNames) return false;

  // Handle different naming variations between user profile and add-on names
  const addonVariations: Record<string, string[]> = {
    'GUT MICROBIOME TEST': ['GUT MICROBE TEST', 'GUT MICROBIOME TEST'],
    'BODY COMPOSITION ANALYSIS': ['BODY COMPOSITION ANALYSIS', 'DEXA'],
    'VO2 MAX & FITNESS TEST': ['VO2 MAX & FITNESS TEST', 'FITNESS TEST'],
    'HEAVY METAL TEST': ['HEAVY METAL TEST'],
    'METABOLIC RISK PROFILING': ['METABOLIC RISK PROFILING', 'METABOLIC RISK PROFILER'],
    LIPOMICS: ['LIPOMICS'],
    SIBO: ['SIBO'],
    METABOLOMICS: ['METABOLOMICS'],
    'GENOME 360': ['GENOME 360'],
    'FOOD ALLERGY TEST': ['FOOD ALLERGY TEST'],
    'EPIGENETIC AGE TESTING': ['EPIGENETIC AGE TESTING'],
  };

  const variations = addonVariations[addonName] || [addonName];
  return variations.some((variation) =>
    userAddonNames.some(
      (userAddon) =>
        userAddon.toUpperCase().includes(variation.toUpperCase()) ||
        variation.toUpperCase().includes(userAddon.toUpperCase())
    )
  );
};

// Main function to filter checklist tasks based on program and user add-ons
export const shouldShowChecklistTask = (
  task: { title: string; label?: string },
  programName: string,
  userAddonNames?: string[]
): boolean => {
  const isAddOnTask = task.title.toUpperCase().includes('ADD ON');

  // Always show non-addon tasks
  if (!isAddOnTask) {
    return true;
  }

  // Extract the add-on name from the task title
  const addonName = extractAddonNameFromTaskTitle(task.title);
  if (!addonName) {
    return false; // If we can't identify the add-on, don't show it
  }

  // Get included add-ons for this program
  const includedAddons = getIncludedAddonsForProgram(programName);

  // Show if it's included in the program OR if the user purchased it
  const isIncludedInProgram = includedAddons.includes(addonName);
  const isUserPurchased = hasUserPurchasedAddon(userAddonNames, addonName);

  return isIncludedInProgram || isUserPurchased;
};
